import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { centsLabel, type ShopCatalogResponse, type ShopProductSummary } from "@gooner/shared";
import { track } from "../../analytics";
import { userApi } from "../../lib/api";
import { addToShopCart, useShopCart } from "../../lib/shopCart";
import { useSession } from "../../lib/session";
import { IconBag, IconChevron } from "../ui/icons";
import { AddToCartButton } from "../components/AddToCartButton";
import { buttonClass } from "../ui/primitives";

/** How many products each landing section shows before its See all link. */
const SECTION_PREVIEW = 4;

/** Your orders and Cart: the two ways out of every shop listing page. */
export function ShopActions() {
  const user = useSession((s) => s.user);
  const cart = useShopCart();
  const inCart = cart.reduce((n, l) => n + l.quantity, 0);
  return (
    <div className="flex flex-wrap gap-2">
      {user ? (
        <Link to="/shop/orders" className={buttonClass()}>
          <IconBag />
          Your orders
        </Link>
      ) : null}
      <Link to="/shop/cart" className={buttonClass(inCart > 0 ? "primary" : "subtle")}>
        Cart{inCart > 0 ? ` (${inCart})` : ""}
      </Link>
    </div>
  );
}

/**
 * The product cards. The photos do the work: no card chrome around them, just the
 * picture, the name and the price, so a product reads as a thing and not as a row of data.
 */
export function ProductGrid({
  products,
  from,
  columns = "lg:grid-cols-4",
}: {
  products: ShopProductSummary[];
  from: string;
  columns?: string;
}) {
  return (
    <ul className={`grid grid-cols-2 gap-x-3 gap-y-8 sm:gap-x-5 ${columns}`}>
      {products.map((p) => (
        <li key={p.slug} className="flex flex-col">
          <Link to={`/shop/${p.slug}`} className="group block flex-1 rounded-lg focus-visible:outline-2 focus-visible:outline-[var(--color-accent)]">
            <div className="relative aspect-[4/5] overflow-hidden rounded-lg bg-[var(--color-panel-2)]">
              {p.images[0] ? (
                <img
                  src={p.images[0]}
                  alt={p.name}
                  loading="lazy"
                  className={`h-full w-full object-cover ${p.soldOut ? "opacity-40" : ""}`}
                />
              ) : null}
              {p.soldOut ? (
                <span className="absolute left-2 top-2 rounded bg-[var(--color-ink)]/85 px-2 py-1 text-xs text-zinc-200">
                  Sold out
                </span>
              ) : p.compareAtCents > p.priceCents ? (
                <span className="absolute right-2 top-2 rounded bg-[#f5c518] px-2 py-1 text-xs font-semibold text-black">
                  Save {centsLabel(p.compareAtCents - p.priceCents)}
                </span>
              ) : null}
            </div>
            <p className="mt-2 text-sm font-medium text-zinc-100 group-hover:text-[var(--color-accent-soft)]">{p.name}</p>
            <p className="text-sm text-zinc-400">
              {centsLabel(p.priceCents)}
              {p.compareAtCents > p.priceCents ? (
                <span className="ml-2 text-zinc-500 line-through">{centsLabel(p.compareAtCents)}</span>
              ) : null}
            </p>
          </Link>
          {p.soldOut ? null : (
            <AddToCartButton
              size="sm"
              className="mt-2 w-full"
              onAdd={() => {
                addToShopCart(p.slug);
                track("shop_cart_added", { slug: p.slug, cents: p.priceCents, from });
              }}
            />
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * `/shop` — the landing page: a section per category, each showing its first few products and
 * a link to see the rest. Everything here comes from data/shop.json on the server; a file with
 * no categories gets one plain grid, as before.
 */
export function Shop() {
  const user = useSession((s) => s.user);
  const [catalog, setCatalog] = useState<ShopCatalogResponse | null>(null);
  const [state, setState] = useState<"loading" | "open" | "closed" | "error">("loading");

  useEffect(() => {
    let cancelled = false;
    userApi
      .shopCatalog()
      .then((r) => {
        if (cancelled) return;
        setCatalog(r);
        setState(r.open ? "open" : "closed");
        if (r.open) track("shop_viewed", { products: r.products.length });
      })
      .catch(() => {
        if (!cancelled) setState("error");
      });
    return () => {
      cancelled = true;
    };
    // The account can change what this viewer may see while the shop is in preview.
  }, [user?.id]);

  if (state === "closed" || state === "error") {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <p className="py-16 text-center text-sm text-zinc-500">
          {state === "closed" ? "The shop isn't open yet." : "The shop didn't load. Reload the page to try again."}
        </p>
      </div>
    );
  }

  const products = catalog?.products ?? [];
  const known = new Set(catalog?.categories.map((c) => c.slug) ?? []);
  const sections = (catalog?.categories ?? [])
    .map((c) => ({ ...c, products: products.filter((p) => p.category === c.slug) }))
    .filter((s) => s.products.length > 0);
  // Anything without a section still sells: it gets one at the end rather than vanishing.
  const loose = products.filter((p) => !known.has(p.category));

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-zinc-100">Shop</h1>
        <ShopActions />
      </header>
      {catalog && products.length === 0 ? (
        <p className="py-16 text-center text-sm text-zinc-500">Nothing for sale right now. Check back soon.</p>
      ) : null}
      {sections.length === 0 ? (
        <ProductGrid products={products} from="grid" columns="lg:grid-cols-3" />
      ) : (
        <div className="space-y-12">
          {sections.map((s) => (
            <section key={s.slug} aria-labelledby={`shop-section-${s.slug}`}>
              <div className="mb-4 flex items-end justify-between gap-3">
                <div className="min-w-0">
                  <h2 id={`shop-section-${s.slug}`} className="text-xl font-semibold text-zinc-100">
                    {s.name}
                  </h2>
                  {s.blurb ? <p className="mt-0.5 text-sm text-zinc-400">{s.blurb}</p> : null}
                </div>
                {s.products.length > SECTION_PREVIEW ? (
                  <Link to={`/shop/category/${s.slug}`} className={buttonClass("subtle", "sm", "shrink-0")}>
                    See all {s.products.length}
                    <IconChevron width={14} height={14} className="-rotate-90" />
                  </Link>
                ) : null}
              </div>
              <ProductGrid products={s.products.slice(0, SECTION_PREVIEW)} from="section" />
            </section>
          ))}
          {loose.length > 0 ? (
            <section aria-labelledby="shop-section-more">
              <h2 id="shop-section-more" className="mb-4 text-xl font-semibold text-zinc-100">
                More
              </h2>
              <ProductGrid products={loose} from="section" />
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}
